import React from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CardHeader from "./CardHeader.jsx";
import { fullName } from "../../../utils/formatter.js";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { isGrantedAny, role } from "shared/src/roles.mjs";

function ProblemInfo({ problem }) {
  return (
    <Typography variant="body1">
      <strong>Problémový klient</strong>
      <br />
      {problem || "-"}
    </Typography>
  );
}

function buildInfoListItems(client, perms) {
  const c = client ?? {};
  const prefs = c.prefs ?? {};
  const requires_czech_speaker = !!prefs.requires_czech_speaker;
  const text_preference = prefs.text_preference ?? null;
  const any_doctor = !!c.any_doctor;
  const doctors = Array.isArray(c.doctors) ? c.doctors : [];

  const items = [];

  // Czech speaking personnel section
  if (perms.canSeeRequiresCzechSpeaker) {
    items.push(
      <Box key="czech-speaker">
        <strong>Česky mluvící personál</strong>
        <br />
        {requires_czech_speaker ? "Ano" : "-"}
      </Box>,
    );
  }

  // Doctor presence section - always show if user has permission
  if (perms.canSeeDoctorNecessary) {
    items.push(
      <Box key="doctor-necessary" sx={{ mt: items.length > 0 ? 3 : 0 }}>
        <strong>Vždy nutná přítomnost lékaře</strong>
        <br />
        {any_doctor || doctors.length > 0 ? "Ano" : "-"}
      </Box>,
    );
  }

  // Preferred doctor section
  if (perms.canSeePreferredDoctor) {
    const doctorsStr = doctors
      .map((doc) => fullName(doc))
      .filter((d) => d != null && d !== "")
      .join(", ");

    items.push(
      <Box key="preferred-doctor" sx={{ mt: 3 }}>
        <strong>Preferovaný lékař</strong>
        <br />
        {doctorsStr || "-"}
      </Box>,
    );
  }

  // Text preference section
  if (perms.canSeeTextPreference) {
    items.push(
      <Box key="text-preference" sx={{ mt: 3 }}>
        <strong>Textová preference</strong>
        <br />
        {text_preference || "-"}
      </Box>,
    );
  }

  return items;
}

function InfoList({ items }) {
  const normalizedItems = Array.isArray(items) ? items : [];

  return (
    <Typography variant="body1" component="div">
      {normalizedItems.length === 0 ? (
        <Box>-</Box>
      ) : (
        normalizedItems.map((item) => item)
      )}
    </Typography>
  );
}

function ImportantInfo({ importantInfoItems }) {
  return (
    <Typography variant="body1">
      <strong>Důležité info</strong>
      <br />
      {importantInfoItems.length === 0 ? "-" : importantInfoItems.join(", ")}
    </Typography>
  );
}

export function PrefCard({ client, displayMinimalInfo = false }) {
  const theme = useTheme();
  const roles = useRoles();

  const perms = {
    canSeeRequiresCzechSpeaker: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
    canSeeDoctorNecessary: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Dental_Assistant,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
    canSeePreferredDoctor: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
    canSeeTextPreference: isGrantedAny(
      roles,
      new Set([
        role.Floor_Manager,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    ),
  };

  const prefs = client?.prefs ?? {};
  const problem = prefs.problem ?? null;
  const items = buildInfoListItems(client, perms, displayMinimalInfo);
  const importantInfoItems =
    client?.importantInfoTags?.map((t) => t.value) ?? [];

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <CardHeader
          title="Preference"
          color={theme.palette.error.main}
          faIcon={faCircleExclamation}
        />
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          {!displayMinimalInfo ? (
            <>
              <ImportantInfo importantInfoItems={importantInfoItems} />
              <ProblemInfo problem={problem} />
            </>
          ) : null}
          <InfoList items={items} />
        </Stack>
      </CardContent>
    </Card>
  );
}
